import { Card, CardContent, CardHeader, CardTitle } from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { Switch } from "@loot-os/ui/components/switch";

/**
 * The box's details tab, in either editor: the name, which is part of the draft and saved by the
 * header's Save, the slug, which is the box's storefront URL and never changes after create, and
 * Featured. The images are on the Art tab.
 */
export function BoxMetadataCard({
  name,
  slug,
  featured,
  onNameChange,
  onFeaturedChange,
  pending,
  readOnly,
}: {
  name: string;
  slug: string;
  /**
   * Absent for a box template: featuring is per storefront, and a template row is one shared
   * record across every tenant that enabled it, so there is no per-tenant answer to store.
   */
  featured?: boolean;
  onNameChange: (value: string) => void;
  onFeaturedChange?: (value: boolean) => void;
  pending: boolean;
  readOnly: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Details</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="meta-name">Name</Label>
            <Input
              id="meta-name"
              type="text"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              disabled={readOnly}
              required
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="meta-slug">Slug</Label>
            <Input id="meta-slug" type="text" value={slug} readOnly disabled />
            <p className="text-xs text-muted-foreground">
              The box's storefront URL, fixed once the box is created.
            </p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          The name is saved with the header's Save, together with any other changes.
        </p>
        {featured !== undefined && onFeaturedChange && (
          // Saves on toggle rather than waiting for Save: it is a switch, and a switch that
          // silently needs a second press to stick reads as broken.
          <div className="flex items-start gap-3 border-t border-border pt-4">
            <Switch
              id="meta-featured"
              checked={featured}
              onCheckedChange={onFeaturedChange}
              disabled={pending || readOnly}
            />
            <div className="grid gap-1">
              <Label htmlFor="meta-featured">Featured</Label>
              <p className="text-sm text-muted-foreground">
                Show this box in the storefront home page's featured row. With none featured, the
                row is hidden. Saves as soon as it is switched.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
